import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

// Algorithme de Dantzig pour trouver le chemin maximal
export const runDantzigMax = (
    nodes, edges, startNode, endNode, setPath, setSteps,
    setShowSteps, setAfficheMin
) => {
    setAfficheMin(false)

    if (nodes.length < 2) {
      toast.error("Veuillez créer au moins deux sommets", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    if (edges.length === 0) {
      toast.error("Veuillez créer au moins une arête", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    if (!startNode || !endNode) {
      toast.error("Veuillez sélectionner un sommet de départ et d'arrivée", {
        position: 'top-right',
        autoClose: 2000,
        closeButton: false,
        hideProgressBar: true,
        transitionDuration: 0,
        theme: 'colored',
        icon: <HighlightOffIcon style={{ color: 'white' }} />
      })
      return
    }
    
    // Créer un graphe adapté à l'algorithme
    const graph = {}
    nodes.forEach(node => {
      graph[node.id] = { name: node.name, connections: {} }
    })
    
    edges.forEach(edge => {
      graph[edge.source].connections[edge.target] = edge.weight
    })
    
    // Algorithme de Dantzig (maximisation)
    const lambda = {}
    const markedNodes = new Set()
    const predecessors = {}
    const stepsList = []
    
    // Initialiser
    lambda[startNode.id] = 0
    markedNodes.add(startNode.id)
    
    // Ajouter l'étape initiale
    stepsList.push({
      step: 0,
      text: `Initialisation: λ(${startNode.name}) = 0`,
      markedNodes: new Set([startNode.id]),
      lambda: { ...lambda },
      connections: [],
      ensembleE: `E₁ = {${startNode.name}}`,
      startNodeName: startNode.name,
      isMaximization: true // Ajout pour distinguer maximisation
    })
    
    let k = 1
let allNodesProcessed = false

while (!allNodesProcessed) {
  let maxLambda = -Infinity
  let nodeToMark = null
  let prevNode = null
  let connectionsEvaluated = []
  
  // Pour chaque sommet marqué
  markedNodes.forEach(markedNodeId => {
    const markedNode = graph[markedNodeId]
    
    Object.entries(markedNode.connections).forEach(([targetId, weight]) => {
      if (!markedNodes.has(parseInt(targetId))) {
        const targetNodeName = nodes.find(n => n.id === parseInt(targetId)).name
        const markedNodeName = nodes.find(n => n.id === markedNodeId).name
        const newLambda = lambda[markedNodeId] + weight
        
        connectionsEvaluated.push({
          from: markedNodeId,
          to: parseInt(targetId),
          fromName: markedNodeName,
          toName: targetNodeName,
          weight: weight,
          calculatedLambda: newLambda
        })
        
        if (newLambda > (lambda[targetId] || -Infinity)) {
          lambda[targetId] = newLambda
          predecessors[targetId] = markedNodeId
        }
      }
    })
  })
  
  // Trouver le sommet avec la valeur lambda maximale
  Object.entries(lambda).forEach(([nodeId, value]) => {
    if (!markedNodes.has(parseInt(nodeId)) && value > maxLambda) {
      maxLambda = value
      nodeToMark = parseInt(nodeId)
    }
  })
  
  if (nodeToMark === null) {
    allNodesProcessed = true
    break
  }
  
  markedNodes.add(nodeToMark)
  prevNode = predecessors[nodeToMark]
  
  const nodeName = nodes.find(n => n.id === nodeToMark).name
  
  const markedNodeNames = Array.from(markedNodes).map(id => 
    nodes.find(n => n.id === id).name
  ).join(', ')
  
  stepsList.push({
    step: k,
    connections: connectionsEvaluated,
    markedNodes: new Set([...markedNodes]),
    lambda: { ...lambda },
    nodeMarked: nodeName,
    maxLambda: maxLambda,
    ensembleE: `E${k+1} = {${markedNodeNames}}`,
    isMaximization: true
  })
  
  k++
}

// Vérifier s'il existe un chemin vers le sommet d'arrivée
if (!lambda[endNode.id] || lambda[endNode.id] === -Infinity) {
  toast.error("Impossible de trouver un chemin entre les sommets sélectionnés", {
    position: 'top-right',
    autoClose: 2000,
    closeButton: false,
    hideProgressBar: true,
    transitionDuration: 0,
    theme: 'colored',
    icon: <HighlightOffIcon style={{ color: 'white' }} />
  })
  return
}

// Fonction pour trouver tous les chemins maximaux
const findAllMaximalPaths = (endNodeId, startNodeId, lambda, graph) => {
  const allPaths = []
  
  const findPaths = (currentId, path, targetDistance) => {
    if (currentId === startNodeId) {
      allPaths.push([...path].reverse())
      return
    }
    
    Object.entries(graph).forEach(([nodeId, nodeData]) => {
      const fromId = parseInt(nodeId)
      if (nodeData.connections[currentId] && 
          lambda[fromId] + nodeData.connections[currentId] === targetDistance) {
        findPaths(fromId, [...path, fromId], lambda[fromId])
      }
    })
  }
  
  findPaths(endNodeId, [endNodeId], lambda[endNodeId])
  return allPaths
}

// Trouver tous les chemins maximaux
const allMaximalPaths = findAllMaximalPaths(endNode.id, startNode.id, lambda, graph)

// Convertir les IDs en objets nodes pour tous les chemins
const allPathNodes = allMaximalPaths.map(pathIds => 
  pathIds.map(id => nodes.find(node => node.id === id))
)

// Utiliser le premier chemin pour l'affichage principal
const pathNodes = allPathNodes[0]

setPath(pathNodes)
setSteps(stepsList)
setShowSteps(true)

// Stocker tous les chemins pour l'affichage
stepsList.allMaximalPaths = allPathNodes

// Ajouter les informations des chemins multiples aux étapes
const lastStep = stepsList[stepsList.length - 1]
lastStep.allMaximalPaths = allPathNodes
lastStep.hasMultiplePaths = allPathNodes.length > 1
    
}